import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiCall } from '../api/client';
import TutorChat from '../components/TutorChat';
import Markdown from '../components/Markdown';
import GrammarDrill from '../components/GrammarDrill';
import { defaultExerciseType, initialExerciseState, isCorrectAnswer, lessonLabel, wordCount, writingCriteria } from '../utils/lessons';

// A single translate-style self-check: type an answer, check it against the lesson's
// `expected` patterns. Reveals the accepted answer(s) once checked.
function instantFeedback(checked, correct, expected, isReading) {
  if (!checked) return null;
  return (
    <p role="status" className={`mt-2 text-sm ${correct ? 'text-green-600' : 'text-amber-700'}`}>
      {correct ? '✓ Correct!' : isReading ? 'Compare your answer with the reference. Your wording may differ.' : 'Not quite. Review the answer and try again.'}{' '}
      <span className="text-amber-600">{isReading ? 'Reference' : 'Accepted'}: {(expected || []).join('  ·  ') || '—'}</span>
    </p>
  );
}

// Static tone classes (full literal strings so Tailwind doesn't purge them).
const EVAL_TONES = {
  green: { box: 'bg-green-50 border-green-200', head: 'text-green-700', label: 'Looks good!' },
  rose: { box: 'bg-rose-50 border-rose-200', head: 'text-rose-700', label: 'Some corrections' },
  amber: { box: 'bg-amber-50 border-amber-200', head: 'text-amber-700', label: 'Feedback' },
};

// Structured tutor evaluation (Phase 3, §5.4) — rendered from JSON, never raw model text.
function EvaluationResult({ result }) {
  const correct = result.correct;
  const t = EVAL_TONES[correct === true ? 'green' : correct === false ? 'rose' : 'amber'];
  return (
    <div className={`mt-3 rounded-lg border p-3 ${t.box}`}>
      <p className={`text-sm font-medium ${t.head}`}>🐝 {t.label}</p>
      {result.corrected && (
        <p className="text-sm text-amber-900 mt-1">
          <span className="text-amber-500">Suggested:</span> {result.corrected}
        </p>
      )}
      {result.explanation && <p className="text-sm text-amber-700 mt-1">{result.explanation}</p>}
      {result.errorTags?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {result.errorTags.map((t, i) => (
            <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-white border border-amber-200 text-amber-600">{t}</span>
          ))}
        </div>
      )}
    </div>
  );
}

// One practice exercise, shaped by its kind: 'translate' (grammar) and 'comprehension'
// (reading) get an instant local self-check (free, offline) against the authored
// answers; 'open' (writing) tasks have no single right answer, so they get a textarea,
// an optional sample-answer reveal, and a self-review checklist. The AI
// evaluation is always an explicit action so the learner understands when it engages
// and to control cost.
function Exercise({ exercise, state, onChange, onCheck, onReview, courseId, lessonId, isAdmin, targetLanguage }) {
  const { token } = useAuth();
  const { checked, correct } = state;
  const isOpen = exercise.type === 'open';
  const [showSample, setShowSample] = useState(false);
  const [evalResult, setEvalResult] = useState(null);
  const [evalLoading, setEvalLoading] = useState(false);
  const [evalError, setEvalError] = useState(null);
  const [showHint, setShowHint] = useState(false);
  const [reviewChecks, setReviewChecks] = useState([]);
  const revision = useRef(0);
  useEffect(() => () => { revision.current += 1; }, []);
  const criteria = writingCriteria(exercise);
  const words = wordCount(state.value);
  const canReview = !!state.value.trim() && words >= (exercise.minWords || 0) && criteria.every((_, i) => reviewChecks[i]);

  function changeAnswer(value) {
    revision.current += 1;
    onChange(value);
    setEvalResult(null);
    setEvalError(null);
    setReviewChecks([]);
  }

  async function askTutor() {
    if (!state.value.trim() || evalLoading) return;
    setEvalError(null);
    setEvalLoading(true);
    const requestRevision = revision.current;
    try {
      const { evaluation } = await apiCall(
        'POST', `/courses/${courseId}/lessons/${lessonId}/tutor`,
        { mode: 'evaluate', message: state.value, exercise: { type: exercise.type, prompt: exercise.prompt, expected: exercise.expected, criteria: exercise.criteria } },
        token
      );
      if (requestRevision === revision.current) setEvalResult(evaluation);
    } catch (err) {
      if (requestRevision === revision.current) setEvalError(err.message || 'Could not get feedback.');
    } finally {
      setEvalLoading(false);
    }
  }

  const placeholder = isOpen
    ? (targetLanguage ? `Write in ${targetLanguage}…` : 'Write your answer…')
    : exercise.type === 'comprehension'
      ? (targetLanguage ? `Answer in ${targetLanguage}…` : 'Type your answer…')
      : (targetLanguage ? `Type your answer in ${targetLanguage}…` : 'Type your answer…');

  return (
    <div className="bg-white rounded-xl border border-amber-100 p-4">
      {exercise.type === 'gap-fill' && <p className="text-xs text-amber-600 mb-2">Fill the blank with only the missing word or words. Accents count.</p>}
      {exercise.type === 'transform' && <p className="text-xs text-amber-600 mb-2">Rewrite the sentence as instructed.</p>}
      <p className="text-amber-800 font-medium mb-2">{exercise.prompt}</p>
      {isOpen ? (
        <textarea
          value={state.value}
          aria-label={exercise.prompt}
          onChange={e => changeAnswer(e.target.value)}
          rows={6}
          placeholder={placeholder}
          className="w-full px-3 py-2 rounded-lg border border-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-400 resize-y"
        />
      ) : (
        <div className="flex gap-2">
          <input
            type="text"
            aria-label={exercise.prompt}
            value={state.value}
            onChange={e => changeAnswer(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') onCheck(); }}
            placeholder={placeholder}
            className={`min-w-0 flex-1 px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 ${
              checked
                ? correct ? 'border-green-300 focus:ring-green-300 bg-green-50' : 'border-rose-300 focus:ring-rose-300 bg-rose-50'
                : 'border-amber-200 focus:ring-amber-400'
            }`}
          />
          <button
            onClick={onCheck}
            disabled={!state.value.trim() || checked}
            className="px-4 py-2 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-800 text-sm font-medium transition"
          >
            Check
          </button>
        </div>
      )}

      {!isOpen && exercise.hint && <button onClick={() => setShowHint(v => !v)} className="mt-2 text-sm text-amber-600 underline">{showHint ? 'Hide hint' : 'Show hint'}</button>}
      {showHint && <p className="mt-2 text-sm text-amber-700">{exercise.hint}</p>}
      {!isOpen && instantFeedback(checked, correct, exercise.expected, exercise.type === 'comprehension')}
      {checked && exercise.explanation && <Markdown className="mt-2 text-sm text-amber-700">{exercise.explanation}</Markdown>}
      {isOpen && <div className="mt-3 space-y-3">
        <p className="text-xs text-amber-600">{words} {words === 1 ? 'word' : 'words'}{exercise.minWords > 0 ? ` · minimum ${exercise.minWords}` : ''}</p>
        <fieldset className="space-y-2">
          <legend className="font-medium text-sm text-amber-800 mb-2">Review your writing</legend>
          {criteria.map((criterion, i) => <label key={i} className="flex items-start gap-2 text-sm text-amber-700">
            <input type="checkbox" checked={!!reviewChecks[i]} disabled={!state.value.trim()}
              onChange={e => { const selected = e.target.checked; setReviewChecks(prev => criteria.map((_, j) => j === i ? selected : !!prev[j])); onReview(false); }} />
            {criterion}
          </label>)}
        </fieldset>
        <button onClick={() => onReview(true)} disabled={!canReview || state.reviewed} className="px-4 py-2 rounded-lg bg-amber-100 text-amber-800 disabled:opacity-50">
          {state.reviewed ? 'Self-review complete' : 'Finish self-review'}
        </button>
        <p className="text-xs text-amber-500">Self-review records your practice; it does not grade your writing.</p>
      </div>}

      {isOpen && exercise.expected?.length > 0 && (
        showSample
          ? <p className="mt-2 text-sm text-amber-600"><span className="text-amber-400">Sample:</span> {exercise.expected.join('  ·  ')}</p>
          : (
            <button onClick={() => setShowSample(true)} className="mt-2 mr-4 text-sm text-amber-500 hover:text-amber-700 underline">
              Show a sample answer
            </button>
          )
      )}

      {/* Explicit AI evaluation — richer feedback than the exact-match check.
          Admin-only while the tutor is in limited preview (it costs real money). */}
      {isAdmin && (
        <button
          onClick={askTutor}
          disabled={!state.value.trim() || state.value.length > 2000 || evalLoading}
          className="mt-2 text-sm text-amber-600 hover:text-amber-800 font-medium disabled:opacity-50"
        >
          {evalLoading ? '🐝 Checking…' : '🐝 Ask the tutor to check'}
        </button>
      )}
      {evalError && <p className="mt-2 text-sm text-rose-600">{evalError}</p>}
      {isAdmin && state.value.length > 2000 && <p className="text-xs text-amber-600">Tutor feedback supports up to 2,000 characters. Shorten your draft to request feedback.</p>}
      {evalResult && <EvaluationResult result={evalResult} />}
    </div>
  );
}

export default function LessonView({ isAdmin }) {
  const { courseId, lessonId } = useParams();
  const navigate = useNavigate();
  const { token, isGuest } = useAuth();
  // The course page refetches progress on mount, so completing just navigates back.
  const onBack = () => navigate(`/courses/${courseId}`);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exStates, setExStates] = useState([]);
  const [completing, setCompleting] = useState(false);
  const [completionError, setCompletionError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);
    setCompletionError('');
    setCompleting(false);
    apiCall('GET', `/courses/${courseId}/lessons/${lessonId}`, null, token)
      .then(d => {
        if (cancelled) return;
        const exercises = (d.lesson.exercises || []).map(ex => ({ ...ex, type: ex.type || defaultExerciseType(d.lesson.type) }));
        setData({ ...d, lesson: { ...d.lesson, exercises } });
        setExStates(exercises.map(initialExerciseState));
      })
      .catch(err => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [courseId, lessonId, token]);

  function setExValue(i, value) {
    setExStates(prev => prev.map((s, j) => (j === i ? { ...s, value, checked: false, correct: false, reviewed: false } : s)));
  }
  function checkEx(i) {
    setExStates(prev => prev.map((s, j) => {
      if (j !== i || !s.value.trim() || s.checked) return s;
      const correct = isCorrectAnswer(s.value, data.lesson.exercises[i].expected);
      return { ...s, checked: true, correct, attempts: s.attempts + 1, firstCorrect: s.firstCorrect ?? correct };
    }));
  }

  const allFinished = !!data && data.lesson.exercises.every((ex, i) => ex.type === 'open'
    ? exStates[i]?.reviewed
    : exStates[i]?.checked);

  async function handleComplete() {
    if (!allFinished || completing) return;
    setCompleting(true);
    setCompletionError('');
    try {
      // Open writing tasks have no local right/wrong, so only self-checkable
      // exercises count toward the recorded score.
      const scored = data.lesson.exercises
        .map((ex, i) => ({ ex, s: exStates[i] }))
        .filter(p => p.ex.type !== 'open');
      const total = scored.length;
      const correct = scored.filter(p => p.s?.firstCorrect).length;
      const exerciseResults = {
        ...(total > 0 ? { score: correct / total } : {}),
        attempts: scored.reduce((sum, p) => sum + (p.s?.attempts || 0), 0),
        writingCompleted: data.lesson.exercises.filter((ex, i) => ex.type === 'open' && exStates[i]?.reviewed).length,
      };
      await apiCall(
        'POST', `/courses/${courseId}/lessons/${lessonId}/complete`,
        { exerciseResults }, token
      );
      onBack();
    } catch (err) {
      setCompletionError(err.message || 'Could not save progress. Please try again.');
      setCompleting(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-amber-50 flex items-center justify-center">
        <p className="text-amber-600">Loading lesson…</p>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen bg-amber-50 flex flex-col items-center justify-center gap-4 px-4">
        <p className="text-amber-700">{error || 'Lesson not found.'}</p>
        <button onClick={onBack} className="text-amber-600 underline">← Back to course</button>
      </div>
    );
  }

  const { lesson, content } = data;
  if (lesson.type === 'grammar-drill') {
    return <GrammarDrill key={`${courseId}-${lessonId}`} courseId={courseId} lesson={lesson} onBack={onBack} />;
  }
  const finishedCount = lesson.exercises.filter((ex, i) => ex.type === 'open' ? exStates[i]?.reviewed : exStates[i]?.checked).length;
  return (
    <div className="min-h-screen bg-amber-50">
      <header className="bg-white border-b border-amber-100 px-4 sm:px-6 py-4 flex items-center gap-3 sticky top-0 z-10">
        <button onClick={onBack} className="text-amber-500 hover:text-amber-700 text-lg flex-shrink-0">←</button>
        <div className="flex-1 min-w-0">
          <p className="text-xs uppercase tracking-wide text-amber-400">{lessonLabel(lesson.type)} lesson</p>
          <h1 className="text-lg sm:text-xl font-bold text-amber-700 truncate">{lesson.title}</h1>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6">
        {/* Objectives */}
        {lesson.objectives?.length > 0 && (
          <div className="bg-amber-100/60 border border-amber-200 rounded-xl p-4">
            <p className="font-semibold text-amber-800 mb-2">In this lesson you'll learn to</p>
            <ul className="list-disc pl-5 space-y-1 text-amber-700 text-sm">
              {lesson.objectives.map((o, i) => <li key={i}>{o}</li>)}
            </ul>
          </div>
        )}

        {/* Authored content is available to every learner. */}
        {content.explanation && <section className="bg-white border border-amber-100 rounded-xl p-5">
          <h2 className="text-lg font-semibold text-amber-800 mb-3">{lesson.type === 'writing' ? 'Writing guidance' : 'Lesson notes'}</h2>
          <Markdown>{content.explanation}</Markdown>
        </section>}
        {content.passage && <section aria-label="Reading passage" className="bg-white border-l-4 border-amber-400 rounded-xl p-5 sm:p-6">
          <h2 className="text-lg font-semibold text-amber-800 mb-3">Reading passage{lesson.languages?.target ? ` · ${lesson.languages.target}` : ''}</h2>
          <Markdown>{content.passage}</Markdown>
        </section>}

        {/* Example bank */}
        {content.examples?.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold text-amber-800 mb-3">Examples</h2>
            <div className="space-y-2">
              {content.examples.map((ex, i) => (
                <div key={i} className="bg-white rounded-lg border border-amber-100 px-4 py-3">
                  <p className="text-amber-900 font-medium">{ex.target ?? ex.fr}</p>
                  {(ex.source ?? ex.en) && <p className="text-amber-500 text-sm mt-0.5">{ex.source ?? ex.en}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Self-check exercises */}
        {lesson.exercises?.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold text-amber-800 mb-3">
              {{ reading: 'Comprehension questions', writing: 'Writing practice' }[lesson.type] || 'Practice'}
            </h2>
            <p className="text-sm text-amber-600 mb-3">{finishedCount} of {lesson.exercises.length} {lesson.type === 'writing' ? 'tasks reviewed' : 'questions checked'}</p>
            {lesson.type === 'reading' && <p className="text-sm text-amber-600 mb-3">Answer using the passage, then compare with the reference and supporting explanation. Different wording can still show the same understanding.</p>}
            <div className="space-y-3">
              {lesson.exercises.map((ex, i) => (
                <Exercise
                  key={`${lessonId}-${i}`}
                  exercise={ex}
                  state={exStates[i] || initialExerciseState()}
                  onChange={v => setExValue(i, v)}
                  onCheck={() => checkEx(i)}
                  onReview={reviewed => setExStates(prev => prev.map((s, j) => i === j ? { ...s, reviewed } : s))}
                  courseId={courseId}
                  lessonId={lessonId}
                  isAdmin={isAdmin}
                  targetLanguage={lesson.languages?.target}
                />
              ))}
            </div>
          </div>
        )}

        <TutorChat key={lessonId} courseId={courseId} lessonId={lessonId} isAdmin={isAdmin} />
        {completionError && <p role="alert" className="text-rose-600 text-sm">{completionError}</p>}
        {!allFinished && !isGuest && <p className="text-sm text-amber-600">{lesson.type === 'writing' ? 'Write and review each response to finish.' : 'Check each answer before completing the lesson.'}</p>}

        {/* Complete / continue */}
        {isGuest ? (
          <p className="text-center text-amber-500 text-sm">Log in to save your progress through the course.</p>
        ) : (
          <button
            onClick={handleComplete}
            disabled={completing || !allFinished}
            className="w-full py-3 rounded-xl font-semibold bg-amber-500 hover:bg-amber-600 text-white transition disabled:opacity-60"
          >
            {completing ? 'Saving…' : 'Mark complete & continue'}
          </button>
        )}
      </div>
    </div>
  );
}
